import React, { useCallback, useEffect, useState } from 'react';
import {
  CkThemeProvider, CkToastProvider, PageHeader, DataTable, Tag, Button, Banner, Dialog, ConfirmDialog, useCkToast,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import { formatHebrewDateTime } from '../../utils/hebrewDate';
import { apiError } from '../../next/ui';

/**
 * שיגורי הודעות — every broadcast sent from the members list / examinees report: how many were sent, failed and heard.
 * A row opens its recipients; "שליחה חוזרת" calls again whoever did not hear, inside the same broadcast.
 */
const DELIVERY_LABELS = { answer: 'הודעה במענה', callback: 'צינתוק והודעה בחזרה' };
const AUDIENCE_LABELS = { husband: 'נייד הבעל', wife: 'נייד האישה', both: 'בעל ואישה' };
const RECIPIENT_STATUS = {
  queued: { label: 'בשליחה', tone: 'info' },
  sent: { label: 'לא שמע עדיין', tone: 'neutral' },
  failed: { label: 'נכשל', tone: 'danger' },
  skipped: { label: 'הוחלף בהודעה חדשה', tone: 'neutral' },
};
const HEARD_LABELS = { outbound: 'שמע במענה', callback: 'שמע בחזרה' };
const POLL_MS = 3000;

const num = (v) => Number(v || 0).toLocaleString('he-IL');
const heardOf = (b) => b.stats.heardOutbound + b.stats.heardCallback;
const unheardOf = (b) => Math.max(0, b.stats.sent + b.stats.failed - heardOf(b));

function StatusTag({ row }) {
  if (row.status === 'running') return <Tag tone="info">רץ</Tag>;
  if (row.status === 'stopped') return <Tag tone="danger" title={row.stoppedReason}>הופסק</Tag>;
  return <Tag tone="success" icon="check">הסתיים</Tag>;
}

function recipientStatus(r) {
  if (r.heardAt) return <Tag tone="success" icon="check">{HEARD_LABELS[r.heardVia] || 'שמע'}</Tag>;
  const s = RECIPIENT_STATUS[r.status] || { label: r.status, tone: 'neutral' };
  return <Tag tone={s.tone} title={r.error || undefined}>{s.label}</Tag>;
}

function Recipients({ broadcast, onClose }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    api.get(`/broadcasts/${broadcast.id}/recipients`)
      .then(({ data }) => setRows(data.data || []))
      .catch((e) => setError(apiError(e, 'שגיאה בטעינת הנמענים')));
  }, [broadcast.id]);
  return (
    <Dialog open onClose={onClose} title={broadcast.title || 'שיגור הודעה'} icon="users" size="lg" footer={<Button variant="ghost" onClick={onClose}>סגירה</Button>}>
      {error ? <Banner tone="danger" icon="alert">{error}</Banner> : null}
      <DataTable
        mode="client"
        rows={rows || []}
        rowKey="id"
        loading={!rows && !error}
        selectable={false}
        storageKey="bchinot-broadcast-recipients"
        exportFilename="נמעני-שיגור"
        emptyTitle="אין נמענים"
        columns={[
          { key: 'memberName', label: 'חבר', render: (r) => r.memberName || <span className="ck-muted">—</span> },
          { key: 'phone', label: 'טלפון', ltr: true, copy: true },
          {
            key: '_status',
            label: 'מצב',
            type: 'enum',
            getValue: (r) => (r.heardAt ? HEARD_LABELS[r.heardVia] || 'שמע' : RECIPIENT_STATUS[r.status]?.label || r.status),
            render: recipientStatus,
          },
          { key: 'heardAt', label: 'מתי שמע', type: 'date', render: (r) => (r.heardAt ? formatHebrewDateTime(r.heardAt) : <span className="ck-muted">—</span>) },
          { key: 'sendCount', label: 'פעמים שנשלח', type: 'number' },
          { key: 'error', label: 'סיבת כשל', render: (r) => r.error || <span className="ck-muted">—</span> },
        ]}
      />
    </Dialog>
  );
}

export default function BroadcastsNext() {
  const toast = useCkToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [opened, setOpened] = useState(null);
  const [resendTarget, setResendTarget] = useState(null);
  const anyRunning = rows.some((r) => r.status === 'running');

  const load = useCallback(async () => {
    setError('');
    try {
      const { data } = await api.get('/broadcasts');
      setRows(data.data || []);
    } catch (e) {
      setError(apiError(e, 'שגיאה בטעינת השיגורים'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!anyRunning) return undefined;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [anyRunning, load]);

  const resend = async () => {
    try {
      await api.post(`/broadcasts/${resendTarget.id}/resend`);
      toast.success('השליחה החוזרת התחילה');
      await load();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בשליחה החוזרת'));
    } finally {
      setResendTarget(null);
    }
  };

  return (
    <>
      <PageHeader
        title="שיגורי הודעות"
        sub="מה נשלח, למי, וכמה שמעו. משגרים מרשימת החברים ומדו״ח הנבחנים"
        more={[{ key: 'refresh', label: 'רענון', icon: 'refresh', onClick: load }]}
      />
      {error ? <Banner tone="danger" icon="alert" action={<Button size="sm" onClick={load}>נסו שוב</Button>}>{error}</Banner> : null}
      <DataTable
        mode="client"
        rows={rows}
        rowKey="id"
        loading={loading}
        selectable={false}
        storageKey="bchinot-broadcasts"
        exportFilename="שיגורי-הודעות"
        emptyTitle="עוד לא שוגרו הודעות"
        emptyText="בחרו נמענים ברשימת החברים או בדו״ח הנבחנים ולחצו «שיגור הודעה»."
        onRowClick={setOpened}
        rowActions={(r) => (r.status !== 'running' && unheardOf(r)
          ? [{ key: 'resend', icon: 'repeat', label: `שליחה חוזרת ל־${num(unheardOf(r))} שלא שמעו`, onClick: () => setResendTarget(r) }]
          : [])}
        columns={[
          { key: 'createdAt', label: 'מועד', type: 'date', render: (r) => formatHebrewDateTime(r.createdAt) },
          {
            key: 'title',
            label: 'הודעה',
            render: (r) => (
              <span>
                {r.title || '—'}
                {r.isTest ? <>{' '}<Tag>בדיקה</Tag></> : null}
              </span>
            ),
          },
          { key: 'targetLabel', label: 'נמענים' },
          { key: '_delivery', label: 'אופן השיגור', type: 'enum', getValue: (r) => DELIVERY_LABELS[r.delivery] || '' },
          { key: '_audience', label: 'למי', type: 'enum', getValue: (r) => AUDIENCE_LABELS[r.audience] || '' },
          { key: '_total', label: 'סה"כ', type: 'number', getValue: (r) => r.stats.total },
          { key: '_sent', label: 'נשלחו', type: 'number', getValue: (r) => r.stats.sent },
          { key: '_failed', label: 'נכשלו', type: 'number', getValue: (r) => r.stats.failed },
          { key: '_heard', label: 'שמעו', type: 'number', getValue: heardOf },
          { key: 'billedUnits', label: 'יחידות שחויבו', type: 'number', render: (r) => (r.billedUnits ? Math.round(r.billedUnits * 10) / 10 : <span className="ck-muted">—</span>) },
          { key: '_status', label: 'מצב', type: 'enum', getValue: (r) => r.status, render: (r) => <StatusTag row={r} /> },
        ]}
      />
      {opened ? <Recipients broadcast={opened} onClose={() => setOpened(null)} /> : null}
      <ConfirmDialog
        open={!!resendTarget}
        onClose={() => setResendTarget(null)}
        onConfirm={resend}
        title="שליחה חוזרת"
        message={resendTarget ? `לשלוח שוב את «${resendTarget.title}» ל־${num(unheardOf(resendTarget))} מספרים שלא שמעו?` : ''}
        confirmLabel="שליחה"
      />
    </>
  );
}

/** the old design has no crm-kit/next theme around the page — supply one */
export function BroadcastsLegacy() {
  return (
    // eslint-disable-next-line react/style-prop-object -- CkThemeProvider's `style` is the design style name
    <CkThemeProvider style="modern" mode="light" accent="#4F46E5" className="bchinot-next">
      <CkToastProvider>
        <div className="nx-stack"><BroadcastsNext /></div>
      </CkToastProvider>
    </CkThemeProvider>
  );
}
